
import { useEffect, useState } from "react";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { toast } from "@/hooks/use-toast";
import { MODEL_PATHS, preloadModels } from "./SceneUtils";

interface FootballFieldProps {
  enhancedTextures?: boolean;
}

export const FootballField = ({ enhancedTextures = false }: FootballFieldProps) => {
  const [modelError, setModelError] = useState(false);
  
  // Preload models when the component mounts
  useEffect(() => {
    console.log("Football Field component mounted with enhanced textures:", enhancedTextures);
    preloadModels();
  }, [enhancedTextures]);
  
  try {
    const { scene } = useGLTF(MODEL_PATHS.field);
    console.log("Field model loaded successfully");
    
    // Apply enhanced materials if requested
    useEffect(() => {
      if (enhancedTextures && scene) {
        console.log("Applying enhanced textures to field");
        scene.traverse((obj) => {
          if (obj instanceof THREE.Mesh) {
            // Enhance material properties
            if (obj.material) {
              obj.material.envMapIntensity = 1.5;
              obj.material.needsUpdate = true;
              // Only apply to grass
              if (obj.material.name?.includes('grass') || obj.material.color?.getHexString() === "2d572c") {
                obj.material.roughness = 0.8;
                obj.material.metalness = 0.1;
              }
            }
          }
        });
      }
    }, [enhancedTextures, scene]);
    
    return (
      <primitive 
        object={scene} 
        position={[0, -1, 0]} 
        scale={[20, 20, 20]}
        receiveShadow
      />
    );
  } catch (error) {
    console.error("Failed to load field model:", error);
    if (!modelError) {
      setModelError(true);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load field model. Using fallback.",
      });
    }
    // Fallback to a simple plane with enhanced materials if requested
    return (
      <mesh 
        rotation={[-Math.PI / 2, 0, 0]} 
        position={[0, -1, 0]} 
        receiveShadow
      >
        <planeGeometry args={[50, 50, enhancedTextures ? 64 : 1, enhancedTextures ? 64 : 1]} />
        <meshStandardMaterial 
          color="#2d572c" 
          roughness={enhancedTextures ? 0.8 : 1} 
          metalness={enhancedTextures ? 0.1 : 0}
          envMapIntensity={enhancedTextures ? 1.5 : 1}
        />
      </mesh>
    );
  }
};
